<script lang="ts">
  import captionsState from '../../../stores/captions.svelte'
  import componentAttributesStore from '../../../stores/componentAttributes.svelte'
  import translationsStore from '../../../stores/translations.svelte'
  import videoStore from '../../../stores/video.svelte'
  import CaptionsIcon from '../icons/CaptionsIcon.svelte'
  import DownloadIcon from '../icons/DownloadIcon.svelte'
  import QualityIcon from '../icons/QualityIcon.svelte'
  import SpeedIcon from '../icons/SpeedIcon.svelte'
  import ReportIcon from '../icons/ReportIcon.svelte'
  import SettingsMenuContainer from '../settings-menu/SettingsMenuContainer.svelte'
  import ButtonEntry from './entries/ButtonEntry.svelte'
  import CheckBoxEntry from './entries/CheckBoxEntry.svelte'
  import ColorSelectorEntry from './entries/ColorSelectorEntry.svelte'
  import GoBackwardEntry from './entries/GoBackwardEntry.svelte'
  import GoForwardEntry from './entries/GoForwardEntry.svelte'
  import PlusMinusEntry from './entries/PlusMinusEntry.svelte'

  type Props = {
    onclose: () => void
  }

  const { onclose }: Props = $props()

  type Section =
    | 'subtitles'
    | 'subtitles-customize'
    | 'speed'
    | 'quality'
    | null

  let openedSection = $state<Section>(null)

  function openSection(section: Section) {
    openedSection = section
  }

  function close(withDelay = false) {
    setTimeout(() => onclose(), withDelay ? 100 : 0)
  }

  function goBack(_: HTMLElement, { duration }: { duration: number }) {
    return {
      duration,
      delay: 100,
      css: (t: number) => {
        const easing = 'cubic-bezier(0.42, 0, 0.58, 1)'
        return `
          transform: translateX(${(1 - t) * -100}%);
          transition: transform ${duration}ms ${easing}, opacity ${duration}ms ${easing};
        `
      },
    }
  }

  function capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  function sanitize(str: string) {
    return str
      .trim()
      .replace(/[^a-z0-9]/gi, '_')
      .replace(/_+/g, '_')
      .toLowerCase()
  }

  function goForward(_: HTMLElement, { duration }: { duration: number }) {
    return {
      duration,
      delay: 100,
      css: (t: number) => {
        const easing = 'cubic-bezier(0.42, 0, 0.58, 1)'
        return `
          transform: translateX(${(1 - t) * 100}%);
          transition: transform ${duration}ms ${easing}, opacity ${duration}ms ${easing};
        `
      },
    }
  }
</script>

<SettingsMenuContainer>
  {#if !openedSection}
    <ul id="settings" class="w-full h-fit p-2" in:goBack={{ duration: 100 }}>
      <li><p class="font-semibold pl-3 mb-3">Settings</p></li>
      <GoForwardEntry
        label="Captions"
        icon={CaptionsIcon}
        hidden={!captionsState.state.subtitleTracks.length}
        onclick={() => openSection('subtitles')}
      />
      <GoForwardEntry
        hidden={videoStore.isLiveStream()}
        icon={SpeedIcon}
        label={translationsStore.state.speed}
        onclick={() => openSection('speed')}
      />
      <GoForwardEntry
        hidden={componentAttributesStore.state.disableQualityPicker}
        icon={QualityIcon}
        label={translationsStore.state.quality}
        onclick={() => openSection('quality')}
      />
      <ButtonEntry
        label={translationsStore.state.report}
        icon={ReportIcon}
        hidden={false}
        onclick={() => {
          window.open(
            `https://${componentAttributesStore.state.abuseDomain}/${componentAttributesStore.state.uiLanguage.startsWith('fr') ? 'fr' : 'en'}/abuse/?url=${encodeURIComponent(window.location.href)}`,
            '_blank',
          )
        }}
      />
      <!--<ButtonEntry
        label="Download"
        icon={DownloadIcon}
        hidden={!videoStore.state.mediaDescriptor?.mp4Url ||
          componentAttributesStore.state.disableDownload}
        onclick={() =>
          videoStore.state.videoController?.downloadMp4(
            sanitize(componentAttributesStore.state.mediaTitle || 'video') + '.mp4',
          )}
      />-->
    </ul>
  {:else if openedSection === 'subtitles'}
    <ul
      id="subtitles"
      class="w-full h-fit p-2"
      in:goForward={{ duration: 120 }}
    >
      <GoBackwardEntry label="Captions" on:click={(e) => openSection(null)} />
      <CheckBoxEntry
        label="Off"
        checked={!captionsState.state.subtitleEnabled}
        onclick={() => {
          captionsState.state.subtitleEnabled = false
          close(true)
        }}
      />
      {#each captionsState.state.subtitleTracks || [] as subtitleTrack}
        <CheckBoxEntry
          label={capitalize(subtitleTrack.label)}
          checked={captionsState.state.subtitleEnabled &&
            captionsState.state.selectedSubtitleTrackId === subtitleTrack.id}
          onclick={() => {
            captionsState.state.selectedSubtitleTrackId = subtitleTrack.id
            captionsState.state.subtitleEnabled = true
            close(true)
          }}
        />
      {/each}
      <GoForwardEntry
        label="Customize"
        onclick={() => openSection('subtitles-customize')}
      />
    </ul>
  {:else if openedSection === 'subtitles-customize'}
    <ul
      id="subtitles-customize"
      class="w-full h-fit p-2"
      in:goForward={{ duration: 120 }}
    >
      <GoBackwardEntry
        label="Captions customization"
        on:click={(e) => openSection('subtitles')}
      />
      <ColorSelectorEntry
        label="Font color"
        colors={[
          '#141414',
          '#FFFFFF',
          '#FF7E58',
          '#FFD258',
          '#589EFF',
          '#CC58FF',
          '#58FF98',
        ]}
        bind:value={captionsState.state.fontColor}
      />
      <ColorSelectorEntry
        label="Background color"
        colors={[
          '#000000CC',
          '#282828CC',
          '#FFFFFFCC',
          '#FF7E58CC',
          '#FFD258CC',
          '#589EFFCC',
          '#CC58FFCC',
          '#58FF98CC',
        ]}
        bind:value={captionsState.state.backgroundColor}
      />
      <PlusMinusEntry
        label="Font size"
        values={['0.5em', '0.8em', '1em', '1.2em', '1.5em', '2em', '3em']}
        bind:value={captionsState.state.fontSize}
      />
    </ul>
  {:else if openedSection === 'speed'}
    <ul id="speed" class="w-full h-fit p-2" in:goForward={{ duration: 120 }}>
      <GoBackwardEntry
        label={translationsStore.state.speed}
        on:click={(e) => openSection(null)}
      />
      {#each [1, 1.5, 2, 3, 5] as speed}
        <CheckBoxEntry
          label={'x ' + speed}
          checked={(componentAttributesStore.state.playbackRate || 1) === speed}
          onclick={() => {
            componentAttributesStore.state.playbackRate = speed
            close(true)
          }}
        />
      {/each}
    </ul>
  {:else if openedSection === 'quality'}
    <ul id="quality" class="w-full h-fit p-2" in:goForward={{ duration: 120 }}>
      <GoBackwardEntry
        label={translationsStore.state.quality}
        on:click={(e) => openSection(null)}
      />
      <CheckBoxEntry
        label="Auto"
        checked={componentAttributesStore.state.forcedQualityLevel === 0}
        onclick={() => {
          componentAttributesStore.state.forcedQualityLevel = 0
          close(true)
        }}
      />
      {#each videoStore.state.qualityLevels.filter((a) => a.height <= (componentAttributesStore.state.maxQuality || Infinity)) as quality}
        <CheckBoxEntry
          label={`${quality.height > quality.width ? quality.width : quality.height}p`}
          checked={componentAttributesStore.state.forcedQualityLevel ===
            quality.height}
          onclick={() => {
            componentAttributesStore.state.forcedQualityLevel = quality.height
            close(true)
          }}
        />
      {/each}
    </ul>
  {/if}
</SettingsMenuContainer>
